// Single source of truth for SmartAB escape-reason labels.
// Consumed by DashboardPage ("Übergaben an SmartAB" panel).
//
// MIRRORS langgraph_nodes.ESCAPE_CODE_LABELS (stable code -> short German label),
// so the dashboard and the backend logs name a reason identically. The codes are
// the contract. Never rename one: practices store them in their
// `smartab_autocomplete_codes` config, and call_statistics.escape_code holds
// historical rows keyed by them.

export const ESCAPE_CODE_LABELS = {
  no_slot_found: 'Kein passender Termin',
  no_slot_within_prescription_deadline: 'Kein Termin in der Verordnungsfrist',
  no_slot_for_move: 'Kein Termin für die Verschiebung',
  series_too_short: 'Terminserie zu kurz',
  booking_flow_disabled: 'Buchung telefonisch deaktiviert',
  cancel_flow_disabled: 'Absage telefonisch deaktiviert',
  move_flow_disabled: 'Verschiebung telefonisch deaktiviert',
  insurance_not_accepted: 'Versicherungsart nicht akzeptiert',
  new_patient: 'Neuer Patient',
  waiting_list: 'Warteliste',
  existing_patient_new_prescription: 'Bestandspatient, neue Verordnung',
  form_link_missing: 'Formularlink fehlt',
  smartab_keyword: 'Schlüsselwort der Praxis',
  therapy_not_offered: 'Therapie nicht im Angebot',
  no_therapist_matched: 'Kein qualifizierter Therapeut',
  identity_unconfirmed: 'Identität nicht bestätigt',
  caller_not_found_in_pvs: 'Anrufer nicht im Praxissystem',
  address_refused: 'Adresse verweigert',
  prescription_not_at_hand: 'Verordnung nicht zur Hand',
  prescription_details_unavailable: 'Verordnungsangaben nicht möglich',
  therapy_not_named: 'Behandlung nicht genannt',
  location_rejected: 'Standort abgelehnt',
  mobile_number_refused: 'Mobilnummer nicht angegeben',
  therapy_not_on_open_prescription: 'Behandlung nicht auf der Verordnung',
  no_prescription_no_selfpay: 'Weder Verordnung noch Selbstzahler',
  expired_prescription: 'Verordnungsfrist abgelaufen',
  no_upcoming_appointments: 'Keine anstehenden Termine',
  prescription_therapy_mismatch: 'Verordnung passt nicht zur Therapie',
  multiple_therapies: 'Mehrere Therapien genannt',
  therapist_preference_unresolved: 'Therapeutenwunsch unklar',
  therapist_pool_excluded: 'Alle Therapeuten ausgeschlossen',
  multiple_open_prescriptions: 'Mehrere offene Verordnungen',
  no_open_prescription: 'Keine offene Verordnung',
  pvs_unreachable: 'Praxissystem nicht erreichbar',
  appointment_lookup_timeout: 'Terminabfrage: Zeitlimit überschritten',
  appointment_lookup_failed: 'Terminabfrage fehlgeschlagen',
  no_prescription_data: 'Keine Verordnungsdaten',
  ongoing_prescription_unconfirmed: 'Laufende Verordnung unbestätigt',
  technical_problem: 'Technisches Problem',
  technical_problem_booking: 'Technisches Problem (Buchung)',
  technical_problem_move: 'Technisches Problem (Verschiebung)',
  move_therapy_not_in_catalog: 'Verschiebung: Behandlungsart nicht im Katalog',
  home_visit_not_bookable: 'Hausbesuch',
  move_verification_blocked: 'Verschiebung angehalten (Prüfung)',
  patient_blocked: 'Patientensperre',
  other_request: 'Anderes Anliegen',
};

// The practice's OWN SmartAB keywords all share one code, so the code alone
// cannot tell "Rückruf" from "Mitarbeiter". This is the one code whose rows stay
// split by their German prose; every other code merges its rows together (their
// prose may carry runtime text like a therapy name, which would otherwise
// fragment one reason across many near-duplicate rows).
export const KEYWORD_CODE = 'smartab_keyword';

// Codes whose prose is "<Grund>: <Zusatzgrund>" (mirrors langgraph_nodes
// SUBREASON_ESCAPE_CODES / _IDENTITY_SUBREASONS). The dashboard lists the Zusatzgrund
// under the code's row; the row KEY stays the code, so the Tasks drill-down still
// lands on every escape of that code.
const SUBREASON_PARENTS = { identity_unconfirmed: 'Identität nicht bestätigt' };
// Rows stored before the Zusatzgrund existed, or with none.
const NO_SUBREASON_LABEL = 'Ohne Zusatzgrund';

// The Zusatzgrund of one escape of a SUBREASON_PARENTS code ("Ohne Zusatzgrund" when its
// prose names none), null for every other code. Expects trimmed prose. Accepts the colon
// form and the older en-dash form ("… – mehrere Patienten mit diesem Namen") so both
// land in one row.
const escapeSubLabel = (code, reason) => {
  const parent = SUBREASON_PARENTS[code];
  if (!parent) return null;
  const m = reason.startsWith(parent) && reason.slice(parent.length).match(/^(?::| –) (.+)$/);
  return m ? m[1].charAt(0).toUpperCase() + m[1].slice(1) : NO_SUBREASON_LABEL;
};

// An unknown code (backend shipped a new one, or the row predates the catalogue)
// falls back to the raw code so it stays visible rather than silently dropped.
export const getEscapeLabel = (code) => ESCAPE_CODE_LABELS[code] || code || 'Unbekannt';

/**
 * Stable identity of one escape display row: the code alone, EXCEPT for the
 * keyword code which appends its German prose.
 *
 * The asymmetry is deliberate and load-bearing for the Dashboard -> Tasks
 * drill-down: the dashboard summary endpoint folds `reason` to NULL for every
 * code except smartab_keyword (and the SUBREASON_PARENTS codes, whose prose only
 * feeds the row's children, never its key), while a task row carries the full prose for
 * EVERY code (and that prose may embed runtime text like a therapy name).
 * Ignoring the prose unless the code is the keyword code is what lets a task
 * row and a dashboard row derive the SAME key from opposite directions.
 */
export const escapeRowKey = (code, reason) => {
  const prose = (reason || '').trim();
  return code === KEYWORD_CODE && prose ? `${code}:${prose}` : code;
};

// Display label for a row key. The ONE label path: consumers hold a key (a filter
// selection restored from localStorage, or one handed over by the Dashboard whose
// task may not be in the loaded window), so labelling from the key keeps every
// caller on one function.
//
// Exact inverse of escapeRowKey: that function appends ':<prose>' for the keyword
// code and nothing else, so stripping that one prefix is total. Splitting on the
// first colon instead would hold only while no code ever contains one, and
// getEscapeLabel explicitly expects unknown codes from a newer backend. A keyword
// row is labelled with the keyword itself; the generic "Schlüsselwort der Praxis"
// would render every one of them identical.
export const escapeLabelFromKey = (key) => {
  const raw = String(key || '');
  return raw.startsWith(`${KEYWORD_CODE}:`)
    ? raw.slice(KEYWORD_CODE.length + 1)
    : getEscapeLabel(raw);
};

/**
 * Collapse the API's (code, reason, count) triples into display rows.
 *
 * Rule: merge by code, EXCEPT smartab_keyword which splits by reason so each of
 * the practice's own keywords gets its own row. Rows come back sorted by count.
 *
 * Returns { rows, total } where a row is { key, label, isKeyword, count, children }.
 * The `key` is the drill-down identity a consumer hands on (see escapeRowKey).
 * `children` ([{ label, count }]) is the Zusatzgrund breakdown of a SUBREASON_PARENTS
 * code, "Ohne Zusatzgrund" last; empty when there is none to show.
 */
export const buildEscapeRows = (breakdown) => {
  const byKey = new Map();
  (breakdown || []).forEach((entry) => {
    const code = entry?.code;
    if (!code) return;
    const count = Number(entry?.count) || 0;
    if (!count) return;
    const isKeyword = code === KEYWORD_CODE;
    const reason = (entry?.reason || '').trim();
    // Keyword rows key on the prose; everything else keys on the code alone.
    const key = escapeRowKey(code, reason);
    const sub = escapeSubLabel(code, reason);
    const existing = byKey.get(key);
    if (existing) {
      existing.count += count;
      if (sub) existing.children[sub] = (existing.children[sub] || 0) + count;
      return;
    }
    byKey.set(key, {
      key,
      label: escapeLabelFromKey(key),
      isKeyword,
      count,
      children: sub ? { [sub]: count } : {},
    });
  });

  const isNone = (label) => (label === NO_SUBREASON_LABEL ? 1 : 0);
  const rows = Array.from(byKey.values()).map((row) => {
    const subs = Object.entries(row.children)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => isNone(a.label) - isNone(b.label) || b.count - a.count);
    // "Ohne Zusatzgrund" alone (rows stored before it existed) adds nothing to the row.
    return { ...row, children: subs.some((s) => !isNone(s.label)) ? subs : [] };
  }).sort((a, b) => b.count - a.count);
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  return { rows, total };
};
